import { property } from '../config/property';
import { cleanPageUrl, getAdvertisingConsent, getMarketingData } from './tracking';
export const SUCCESS_MESSAGE = 'Your contact form was submitted. Please wait for Fanny to get in touch. Your showing is not booked until you receive confirmation.';
export const QUESTION_SUCCESS_MESSAGE = 'Your question was submitted. Please wait for Fanny to get in touch.';
// The lead form posts the full JSON payload straight to this Activepieces webhook.
// This is the standard (non-/sync) URL, so Activepieces accepts the lead immediately and never times out.
const LEAD_WEBHOOK_URL = 'https://cloud.activepieces.com/api/v1/webhooks/qPAFmRghFh1Q5qMoeIPx6';
const pendingKey = 'azalea-pending-submission-v2';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
let pending = null;
const clean = (v, max = 2000) => String(v || '').trim().replace(/<[^>]*>/g, '').slice(0, max);
export function normalizePhone(value = '') {
  const raw = value.trim();
  if (!raw || !/^\+?[\d\s().-]+$/.test(raw)) return '';
  let digits = raw.replace(/\D/g, '');
  if (!raw.startsWith('+') && digits.length === 10) digits = `1${digits}`;
  if (!raw.startsWith('+') && !/^1\d{10}$/.test(digits)) return '';
  return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : '';
}
// Only the contact detail that matches the chosen reply method is required.
// Errors are added in the order the fields appear so the first one receives focus.
export function validateLead(lead) {
  const errors = {};
  if (!['In-person showing', 'Property question'].includes(lead.interest)) errors.interest = 'Choose your next step.';
  if (!['Call', 'Email', 'Text'].includes(lead.contactPreference)) errors.contactPreference = 'Choose how you would like a reply.';
  if (!clean(lead.firstName, 80)) errors.firstName = 'Enter your first name.';
  if (!clean(lead.lastName, 80)) errors.lastName = 'Enter your last name.';
  const email = (lead.email || '').trim();
  if (lead.contactPreference === 'Email') {
    if (!email) errors.email = 'Enter your email address so Fanny can reply.';
    else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address.';
  }
  if (['Call', 'Text'].includes(lead.contactPreference)) {
    if (!(lead.phone || '').trim()) errors.phone = 'Enter your phone number so Fanny can reply.';
    else if (!normalizePhone(lead.phone)) errors.phone = 'Use a 10-digit US number or include the country code.';
  }
  return errors;
}
const toHex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
function randomToken() {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(bytes);
  else bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
  return toHex(bytes);
}
// The website generates the event ID. It is sent to the webhook and reused for the Meta Lead event.
function newEventId() {
  return `lead_${Date.now()}_${randomToken()}`;
}
// Falls back to the raw JSON if SubtleCrypto is unavailable (non-secure context) so the form never breaks.
async function leadDigest(lead) {
  const text = JSON.stringify(lead);
  try {
    return toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))));
  } catch { return text; }
}
async function submissionIdentity(lead) {
  const digest = await leadDigest(lead);
  if (!pending) { try { pending = JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { /* Memory fallback. */ } }
  // An unchanged retry after a failure keeps its ID; any new or changed request gets a fresh one.
  if (!pending || pending.digest !== digest || Date.now() - pending.created > 1800000) {
    pending = { digest, id: newEventId(), timestamp: new Date().toISOString(), created: Date.now() };
    try { sessionStorage.setItem(pendingKey, JSON.stringify(pending)); } catch { /* Memory still prevents duplicate retries. */ }
  }
  return pending;
}
// Called after a successful send so the next request gets its own submission id.
export function resetSubmission() {
  pending = null;
  try { sessionStorage.removeItem(pendingKey); } catch { /* Nothing stored. */ }
}
export async function createPayload(lead) {
  const identity = await submissionIdentity(lead);
  // Omitted or invalid contact details are sent as empty strings, never as made-up values.
  const email = clean(lead.email, 254).toLowerCase();
  return {
    event: 'property_lead',
    event_id: identity.id,
    submission_id: identity.id,
    property: { address: property.address, city: property.city, state: property.state, zip: property.zip, asking_price: property.price },
    lead: { first_name: clean(lead.firstName, 80), last_name: clean(lead.lastName, 80), email: EMAIL_PATTERN.test(email) ? email : '', phone: normalizePhone(lead.phone), interest: lead.interest, purchase_timeline: clean(lead.timeline, 50), contact_preference: lead.contactPreference, working_with_agent: clean(lead.workingWithAgent, 10), showing_preference: clean(lead.availability, 500), message: clean(lead.message) },
    consent: { advertising: getAdvertisingConsent() === 'granted' },
    marketing: getMarketingData(),
    submission: { id: identity.id, timestamp: identity.timestamp, page_url: cleanPageUrl() },
    tracking: { event_id: identity.id }, website: clean(lead.website, 100),
  };
}
export async function sendLead(payload) {
  const response = await fetch(LEAD_WEBHOOK_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  // 408/504 mean the request reached Activepieces but the flow did not reply in time, so the lead was received.
  if (!response.ok && response.status !== 408 && response.status !== 504) {
    if (response.status === 404) throw new Error('The lead webhook could not be found. Please check the webhook URL.');
    let error = '';
    try {
      const body = await response.json();
      error = typeof body?.error === 'string' ? body.error : '';
    } catch { /* Some hosts return HTML instead of JSON. */ }
    throw new Error(error || `Inquiry delivery failed (HTTP ${response.status}).`);
  }
  // "confirmed" is true only for a real 2xx response; the Meta Lead event waits for it.
  return { success: true, confirmed: response.ok, submission_id: payload.submission_id };
}
export function getSuccessMessage(interest) {
  return { title: 'Request received', message: interest === 'Property question' ? QUESTION_SUCCESS_MESSAGE : SUCCESS_MESSAGE };
}